import {
  useCallback, useEffect, useState,
} from 'react';
import type { LatLng } from '~/_shared/types/latLng';
import type { MapObjectManager } from '../../private/mapObjectManager';
import type {
  ShapeSubmitOption, ShapeSubmitOptions,
} from '../drawMapShape.container';
import { useDrawMapShapeContext } from './drawMapShapeContext';

export type UseClickMapShapeDrawProps = {
  active: boolean;
  manager: MapObjectManager;
  submitOptions: ShapeSubmitOptions;
  onDone?: (how: ShapeSubmitOption) => void;
  snap: (point: LatLng) => LatLng | null;
};

export const useClickMapShapeDraw = ({
  active,
  manager,
  submitOptions,
  onDone,
  snap,
}: UseClickMapShapeDrawProps) => {
  const { shape, addOutline } = useDrawMapShapeContext();
  const [firstOutlineHover, setFirstOutlineHover] = useState(false);

  useEffect(() => {
    if (!shape) {
      setFirstOutlineHover(false);
    }
  }, [shape]);

  const onMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!active || !shape || firstOutlineHover || !event.latLng) {
      return;
    }
    const mouseLatLng = { lat: event.latLng.lat(), lng: event.latLng.lng() };
    addOutline(snap(mouseLatLng) ?? mouseLatLng);
  }, [active, shape, firstOutlineHover, addOutline, snap]);

  useEffect(() => {
    if (!active) {
      return;
    }
    const clickListener = manager.addMapClickListener(onMapClick);
    return () => {
      clickListener.remove();
    };
  }, [active, onMapClick, manager]);

  useEffect(() => { // First outline hover — register finish drawing on outline click
    if (!active || !submitOptions.FirstOutline || !shape || shape.outlines.length < 3) {
      return;
    }
    const firstOutline = shape.outlines[0];
    if (!firstOutline) {
      return;
    }

    const mouseOverCleanup = manager.addOutlineMarkerEventListener(firstOutline.id, 'mouseover',
      () => setFirstOutlineHover(true));
    const mouseOutCleanup = manager.addOutlineMarkerEventListener(firstOutline.id, 'mouseout',
      () => setFirstOutlineHover(false));
    const mouseClickCleanup = manager.addOutlineMarkerEventListener(firstOutline.id, 'click',
      // Defer onDone so the trailing map 'click' (fired after 'mouseup'-based webbgl
      // outline click) is processed against the still-mounted shape and dropped
      // by the firstOutlineHover guard, instead of seeding a new shape.
      () => setTimeout(() => onDone?.('FirstOutline'), 0));

    return () => {
      mouseOverCleanup();
      mouseOutCleanup();
      mouseClickCleanup();
    };
  }, [active, shape, manager, submitOptions.FirstOutline, onDone]);

  useEffect(() => { // Finish drawing on double click
    if (!active || !shape) {
      return;
    }
    const onDblClick = () => onDone?.('DoubleClick');
    if (submitOptions.DoubleClick && !firstOutlineHover) {
      document.addEventListener('dblclick', onDblClick);
    }
    return () => {
      document.removeEventListener('dblclick', onDblClick);
    };
  }, [active, shape, firstOutlineHover, submitOptions.DoubleClick, onDone]);
};
